import { network } from "../config";
import {
  DialogChat,
  DialogChatFilters,
  DialogFileInfo,
  DialogImageInfo,
  DialogVoiceMessage,
  EditDialogMessagePayload,
  GenerateDialogVoiceUploadUrlPayload,
  GetDialogMessagesAroundMessagePayload,
  SearchDialogMessagesPayload,
  SendDialogMessagePayload,
  SendDialogTextMessagePayload,
  SendDialogVoiceMessagePayload,
  TypeDialogMessage,
} from "../types/DialogChat";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import { TypeMessage } from "../types/TypeMessageChat";
import { TypeSpecialistFilter } from "../types/TypeSpecialist";
import url from "../utils/formApiUrl";
import socket from "../utils/socket";
import customKy from "./kyExtend";

const { chat } = network;
const { users } = network;

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "dialog-chats/";
const usersBaseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "users/";

const getAuthHeader = (token: string) => {
  return { Authorization: `Bearer ${token}` };
};

export default class DialogChatAPI {
  static async createDialogChat(
    token: string,
    payload: {
      userId?: string;
      userIdToSend: string;
    },
  ): Promise<TypeDefaultResponse & { chat?: DialogChat }> {
    return await customKy
      .post(url(baseURL, chat.createDialogChat), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async getUsersDialogChats(
    token: string,
    currentUserId: string,
    params: {
      page: number;
      limit: number;
      filters?: DialogChatFilters;
    },
  ): Promise<TypeDefaultResponse & { chats: DialogChat[] }> {
    const searchParams = new URLSearchParams();
    if (params.filters?.name) {
      searchParams.append("name", params.filters.name);
    }
    if (params.filters?.isGroupChat !== undefined) {
      searchParams.append("isGroupChat", String(params.filters.isGroupChat));
    }

    return await customKy
      .get(
        url(
          usersBaseURL,
          users.getUsersDialogChats,
          {
            id: currentUserId,
            page: params.page,
            limit: params.limit,
          },
          searchParams,
        ),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getPinnedDialogChats(
    token: string,
    currentUserId: string,
    params: {
      page: number;
      limit: number;
      filters?: DialogChatFilters;
    },
  ): Promise<TypeDefaultResponse & { chats: DialogChat[] }> {
    const searchParams = new URLSearchParams();
    if (params.filters?.name) {
      searchParams.append("name", params.filters.name);
    }
    if (params.filters?.isGroupChat !== undefined) {
      searchParams.append("isGroupChat", String(params.filters.isGroupChat));
    }

    return await customKy
      .get(
        url(
          usersBaseURL,
          users.getPinnedDialogChats,
          {
            id: currentUserId,
            page: params.page,
            limit: params.limit,
          },
          searchParams,
        ),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getDialogMessagesById(
    token: string,
    chatId: string,
    limit: number,
    page: number,
    options?: RequestInit,
  ): Promise<
    TypeDefaultResponse & {
      messages: TypeDialogMessage[];
    }
  > {
    return await customKy
      .get(
        url(baseURL, chat.getDialogMessagesById, { id: chatId, page, limit }),
        {
          headers: getAuthHeader(token),
          ...options,
        },
      )
      .then((res) => res.json());
  }

  static async getDialogMessageById(
    token: string,
    messageId: string,
  ): Promise<
    TypeDefaultResponse & {
      responseMessage?: TypeDialogMessage;
    }
  > {
    return await customKy
      .get(url(baseURL, chat.getDialogMessageById, { id: messageId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getMessageFiles(
    token: string,
    id: string,
  ): Promise<
    TypeDefaultResponse & {
      files: DialogFileInfo[];
    }
  > {
    return await customKy
      .get(url(baseURL, chat.getMessageFiles, { id }), {
        headers: getAuthHeader(token),
        timeout: 30000,
      })
      .then((res) => res.json());
  }

  static async getMessageImages(
    token: string,
    id: string,
  ): Promise<
    TypeDefaultResponse & {
      images?: DialogImageInfo[];
    }
  > {
    return await customKy
      .get(url(baseURL, chat.getMessageImages, { id }), {
        headers: getAuthHeader(token),
        timeout: 30000,
      })
      .then((res) => res.json());
  }

  static async generateDialogVoiceUploadUrl(
    token: string,
    payload: GenerateDialogVoiceUploadUrlPayload,
  ): Promise<TypeDefaultResponse & { url?: string; voice?: DialogVoiceMessage }> {
    const { dialogChatId, ...json } = payload;

    return await customKy
      .post(url(baseURL, chat.generateDialogVoiceUploadUrl, { id: dialogChatId }), {
        headers: getAuthHeader(token),
        json,
      })
      .then((res) => res.json());
  }

  static async sendMessage(payload: SendDialogMessagePayload): Promise<
    TypeDefaultResponse & {
      newMessage?: TypeMessage | null;
    }
  > {
    return new Promise((resolve) => {
      socket.emit(chat.sendMessageToDialogChat, payload);
      socket.once(chat.onDialogMessage, (response) => {
        resolve(response);
      });
    });
  }

  static async sendMessageToDialogChat(
    payload: SendDialogTextMessagePayload | SendDialogVoiceMessagePayload,
  ): Promise<
    TypeDefaultResponse & {
      newMessage?: TypeDialogMessage | null;
      responseMessage?: TypeDialogMessage | null;
    }
  > {
    return new Promise((resolve) => {
      socket.emit(chat.sendMessageToDialogChat, payload);
      socket.once(chat.onDialogMessage, (response) => {
        resolve(response);
      });
    });
  }

  static async markMessagesAsRead(
    token: string,
    messageIds: string[],
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, chat.markMessagesAsRead), {
        headers: getAuthHeader(token),
        json: { messageIds },
      })
      .then((res) => res.json());
  }

  static async searchDialogMessages(
    token: string,
    payload: SearchDialogMessagesPayload,
  ): Promise<TypeDefaultResponse & { messages?: TypeDialogMessage[] }> {
    const { chatId, ...json } = payload;

    return await customKy
      .post(url(baseURL, chat.searchDialogMessages, { id: chatId }), {
        headers: getAuthHeader(token),
        json,
      })
      .then((res) => res.json());
  }

  static async getDialogMessagesAroundMessage(
    token: string,
    payload: GetDialogMessagesAroundMessagePayload,
  ): Promise<
    TypeDefaultResponse & {
      page?: number;
      limit?: number;
      targetMessageId?: string;
      messages?: TypeDialogMessage[];
    }
  > {
    const { chatId, messageId, limit } = payload;

    return await customKy
      .get(
        url(baseURL, chat.getDialogMessagesAroundMessage, {
          id: chatId,
          messageId,
        }),
        {
          headers: getAuthHeader(token),
          searchParams: { limit },
        },
      )
      .then((res) => res.json());
  }

  static async editDialogMessage(
    token: string,
    payload: EditDialogMessagePayload,
  ): Promise<
    TypeDefaultResponse & {
      updatedMessage?: TypeDialogMessage;
      userIdsToSend?: string[];
    }
  > {
    const { messageId, ...json } = payload;

    return await customKy
      .patch(url(baseURL, chat.editDialogMessage, { id: messageId }), {
        headers: getAuthHeader(token),
        json,
      })
      .then((res) => res.json());
  }

  static async deleteDialogMessage(
    token: string,
    messageId: string,
  ): Promise<
    TypeDefaultResponse & {
      messageId?: string;
      deletedBy?: string;
      userIdsToSend?: string[];
    }
  > {
    return await customKy
      .delete(url(baseURL, chat.deleteDialogMessage, { id: messageId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async pinDialogMessage(
    token: string,
    messageId: string,
  ): Promise<
    TypeDefaultResponse & {
      pinnedMessage?: {
        message: string;
        pinnedBy: string;
        pinnedAt: string;
      };
      userIdsToSend?: string[];
    }
  > {
    return await customKy
      .post(url(baseURL, chat.pinDialogMessage, { id: messageId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async unpinDialogMessage(
    token: string,
    messageId: string,
  ): Promise<TypeDefaultResponse & { messageId?: string; userIdsToSend?: string[] }> {
    return await customKy
      .delete(url(baseURL, chat.unpinDialogMessage, { id: messageId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async pinDialogChat(
    token: string,
    dialogChatId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, chat.pinDialogChat, { id: dialogChatId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async unpinDialogChat(
    token: string,
    dialogChatId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .delete(url(baseURL, chat.unpinDialogChat, { id: dialogChatId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async startTypingInDialogChat(
    token: string,
    dialogChatId: string,
  ): Promise<TypeDefaultResponse & { userIdsToSend?: string[] }> {
    return await customKy
      .post(url(baseURL, chat.startTypingInDialogChat, { id: dialogChatId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async stopTypingInDialogChat(
    token: string,
    dialogChatId: string,
  ): Promise<TypeDefaultResponse & { userIdsToSend?: string[] }> {
    return await customKy
      .post(url(baseURL, chat.stopTypingInDialogChat, { id: dialogChatId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async downloadMessageFile(messageFileId: string): Promise<Blob> {
    return await customKy
      .get(url(baseURL, chat.downloadMessageFile, { id: messageFileId }))
      .then((res) => res.blob());
  }

  static async getMessageFileStream(messageFileId: string): Promise<Blob> {
    return await customKy
      .get(url(baseURL, chat.getMessageFileStream, { id: messageFileId }))
      .then((res) => res.blob());
  }

  static async getUnreadDialogMessagesLength(token: string): Promise<
    TypeDefaultResponse & {
      amount: number;
    }
  > {
    return await customKy
      .get(url(baseURL, chat.getUnreadDialogMessagesLength), {
        headers: getAuthHeader(token),
        timeout: 30000,
      })
      .then((res) => res.json());
  }

  static async muteChat(
    token: string,
    dialogChatId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, chat.muteChat, { id: dialogChatId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async blockDialogChat(
    token: string,
    dialogChatId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, chat.blockDialogChat, { id: dialogChatId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async leaveChat(
    token: string,
    dialogChatId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, chat.leaveChat, { id: dialogChatId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async distributeAdminMessage(
    token: string,
    payload: {
      userId: string;
      filters: TypeSpecialistFilter;
      message: string;
      files?: File[];
      labels?: { text: string; language: string }[];
    },
  ): Promise<TypeDefaultResponse> {
    const formData = new FormData();
    if (payload.files && payload.files.length) {
      for (let i = 0; i < payload.files.length; i++) {
        formData.append("files", payload.files[i]);
      }
    }
    formData.append("userId", payload.userId);
    formData.append("filters", JSON.stringify(payload.filters));
    formData.append("message", payload.message);
    if (payload.labels) {
      formData.append("labels", JSON.stringify(payload.labels));
    }

    return await customKy
      .post(url(baseURL, chat.distributeAdminMessage), {
        headers: getAuthHeader(token),
        body: formData,
      })
      .then((res) => res.json());
  }
}
